import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';

// Elements
import Container from 'react-bootstrap/Container';
import { CountryCodes } from '../Helper/constants';

// Groups with more items than this open as a two-column panel on desktop.
const WIDE_MENU_THRESHOLD = 6;

// Renders an internal route with <Link>, anything else with a plain <a>.
function MenuLink({ entry, className, onClick, children }) {
    const current = entry.to && window.location.pathname === entry.to ? 'page' : undefined;
    if(entry.to) {
        return <Link to={entry.to} className={className} onClick={onClick} aria-current={current}>{children}</Link>;
    }
    return <a href={entry.href} className={className} onClick={onClick}>{children}</a>;
}

export default class Header extends Component {

    state = {
        menuOpen: false,
        openGroup: null,
    };

    toggleRef = React.createRef();
    closeRef = React.createRef();

    componentWillUnmount() {
        this.unlockPage();
    }

    onKeyDown = (event) => {
        if(event.key === 'Escape') {
            this.closeMenu();
        }
    }

    lockPage() {
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeyDown);
    }

    unlockPage() {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.onKeyDown);
    }

    openMenu = () => {
        this.setState({ menuOpen: true, openGroup: null }, () => {
            this.lockPage();
            if(this.closeRef.current) {
                this.closeRef.current.focus();
            }
        });
    }

    closeMenu = () => {
        if(!this.state.menuOpen) {
            return;
        }
        this.setState({ menuOpen: false }, () => {
            this.unlockPage();
            if(this.toggleRef.current) {
                this.toggleRef.current.focus();
            }
        });
    }

    // Flip a dropdown to open right-aligned if it would run past the screen edge.
    fitMenu = (event) => {
        const panel = event.currentTarget.querySelector('.site-nav__menu');
        if(!panel) {
            return;
        }
        panel.classList.remove('flip-end');
        const rect = panel.getBoundingClientRect();
        if(rect.right > document.documentElement.clientWidth - 8) {
            panel.classList.add('flip-end');
        }
    }

    toggleGroup(key) {
        this.setState(state => ({ openGroup: state.openGroup === key ? null : key }));
    }

    // Same items and conditions as the previous menu.
    buildMenu() {
        const clinic = this.props.clinic;
        const menu = [];
        if(clinic.team.length !== 0) {
            menu.push({ key: 'about', label: 'About us', to: '/about' });
        }
        menu.push({ key: 'appointment', label: 'Appointment', to: '/appointment' });
        if(clinic.gallery.general.length !== 0) {
            menu.push({ key: 'gallery', label: 'Gallery', to: '/gallery' });
        }
        menu.push({
            key: 'services',
            label: 'Services',
            children: clinic.treatments.map(service => ({
                key: service.name,
                label: service.name_short,
                href: service.url ? service.url : `/treatments/${service.id}`,
            })),
        });
        clinic.pages.filter(category => category.name !== 'UNCAT').forEach(category => {
            if(category.type === 'category') {
                if(category.items && category.items.length > 0) {
                    menu.push({
                        key: `category-${category.name}`,
                        label: category.name,
                        children: category.items.map(item => ({ key: item.name, label: item.name, href: item.url })),
                    });
                }
            } else if(category.url && category.url.startsWith('/page/')) {
                menu.push({ key: `page-${category.name}`, label: category.name, to: category.url });
            } else {
                menu.push({ key: `page-${category.name}`, label: category.name, href: category.url });
            }
        });
        if(clinic.abdm && clinic.abdm.enabled) {
            menu.push({ key: 'abha', label: 'ABHA', href: '/abdm/enroll' });
        }
        if((clinic.overrides && clinic.overrides.dashboard_link) || !clinic.overrides) {
            menu.push({ key: 'dashboard', label: 'Dashboard', href: '/dashboard/login' });
        }
        return menu;
    }

    renderDesktop(menu) {
        return (
            <nav className="site-nav" aria-label="Main">
                <ul className="site-nav__list">
                    {menu.map((entry, index) => {
                        if(!entry.children) {
                            return (
                                <li className="site-nav__item" key={entry.key}>
                                    <MenuLink entry={entry} className="site-nav__link">{entry.label}</MenuLink>
                                </li>
                            );
                        }
                        const wide = entry.children.length > WIDE_MENU_THRESHOLD;
                        const align_end = index >= menu.length - 2;
                        return (
                            <li className="site-nav__item has-menu" key={entry.key} onMouseEnter={this.fitMenu} onFocus={this.fitMenu}>
                                <button type="button" className="site-nav__link" aria-haspopup="true">
                                    {entry.label}<i className="fa-solid fa-chevron-down site-nav__chevron"></i>
                                </button>
                                <div className={'site-nav__menu' + (wide ? ' is-wide' : '') + (align_end ? ' align-end' : '')}>
                                    {entry.children.map(child => (
                                        <MenuLink entry={child} className="site-nav__menu-link" key={child.key}>{child.label}</MenuLink>
                                    ))}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </nav>
        );
    }

    renderMobile(menu) {
        const clinic = this.props.clinic;
        const open = this.state.menuOpen;
        const mobile = clinic.contact.mobile.length > 0 ? clinic.contact.mobile[0] : null;
        return (
            <div className={'mobile-menu' + (open ? ' is-open' : '')} role="dialog" aria-modal="true" aria-label="Menu" aria-hidden={!open}>
                <svg className="mobile-menu__accent" viewBox="0 0 400 400" aria-hidden="true" focusable="false">
                    {[190, 150, 110, 70].map(r => <circle key={r} cx="200" cy="200" r={r}/>)}
                </svg>
                <div className="mobile-menu__top">
                    <Link to="/" onClick={this.closeMenu} className="mobile-menu__logo">
                        <img src={clinic.logo.path} alt={clinic.name || 'clinic logo'}/>
                    </Link>
                    <button type="button" className="mobile-menu__close" aria-label="Close menu" ref={this.closeRef} onClick={this.closeMenu}>
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>
                <ul className="mobile-menu__list">
                    {menu.map((entry, index) => (
                        <li key={entry.key} style={{ '--i': index }}>
                            {entry.children ?
                            <>
                                <button type="button" className="mobile-menu__link" aria-expanded={this.state.openGroup === entry.key}
                                    onClick={() => this.toggleGroup(entry.key)}>
                                    {entry.label}<i className="fa-solid fa-chevron-down mobile-menu__chevron"></i>
                                </button>
                                <div className={'mobile-menu__sub' + (this.state.openGroup === entry.key ? ' is-open' : '')}>
                                    <div>
                                        {entry.children.map(child => (
                                            <MenuLink entry={child} className="mobile-menu__sublink" key={child.key} onClick={this.closeMenu}>{child.label}</MenuLink>
                                        ))}
                                    </div>
                                </div>
                            </> :
                            <MenuLink entry={entry} className="mobile-menu__link" onClick={this.closeMenu}>{entry.label}</MenuLink>}
                        </li>
                    ))}
                </ul>
                <div className="mobile-menu__actions">
                    <Link to="/appointment" className="ui-btn ui-btn--light" onClick={this.closeMenu}>
                        <i className="fa-regular fa-calendar-check"></i>
                        <span>Book Appointment</span>
                        <i className="fa-solid fa-arrow-right ui-btn__arrow"></i>
                    </Link>
                    {mobile ?
                    <div className="mobile-menu__contact">
                        <a href={`tel:${CountryCodes.india}${mobile}`} className="ui-btn ui-btn--glass"><i className="fa-solid fa-phone"></i><span>Call</span></a>
                        <a href={`https://wa.me/${CountryCodes.india.replace('+', '')}${mobile}`} target="_blank" rel="noopener noreferrer" className="ui-btn ui-btn--glass">
                            <i className="fa-brands fa-whatsapp"></i><span>WhatsApp</span>
                        </a>
                    </div> : null}
                </div>
            </div>
        );
    }

    render() {
        const menu = this.buildMenu();
        return (
        <div className="header-wrapper">
            <Container className="site-header__inner">
                <Link to="/" className="site-header__logo">
                    <img src={this.props.clinic.logo.path} alt="clinic logo"/>
                </Link>
                {this.renderDesktop(menu)}
                <button type="button" className="site-nav__toggle" aria-label="Open menu" aria-expanded={this.state.menuOpen}
                    ref={this.toggleRef} onClick={this.openMenu}>
                    <span></span><span></span><span></span>
                </button>
            </Container>
            {ReactDOM.createPortal(this.renderMobile(menu), document.body)}
        </div>
        );
    }
}
